import { FC, useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { CustomButton } from "../../atoms/button/button";
import { Colors, GET_PINTAG_BOXES, OPEN_PINTAG_BOX } from "../../../configs";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { BaseResponse, BaseResponseSingle } from "../../../typings/interfaces";
import { IPintagBoxStatus } from "../../../typings/interfaces/pos/pintag";

const TAGS_PER_BOX = 1000;

export interface IPinTagBoxesProps {
  /** Manager who approved access with their PIN (POS); recorded as the opener */
  openedByUserId?: number;
}

/**
 * Each pin tag colour's current box of tags 000–999 (reported issues 3.p).
 * A tag is in use only within the current box; opening a new box makes every
 * number available again. Used by the admin panel and the POS manager page.
 */
export const PinTagBoxes: FC<IPinTagBoxesProps> = ({ openedByUserId }) => {
  const snackbar = useSnackbarContext();
  const [boxes, setBoxes] = useState<IPintagBoxStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<IPintagBoxStatus | null>(null);
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res =
        await axios.get<BaseResponse<IPintagBoxStatus>>(GET_PINTAG_BOXES);
      if (res.data?.status !== "SUCCESS") {
        throw new Error(res.data?.message || "Could not load pin tag boxes");
      }
      setBoxes(res.data.body ?? []);
    } catch (err: any) {
      snackbar.showSnackbar(
        err?.message || "Could not load pin tag boxes",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenBox = async () => {
    if (!confirm) return;
    setOpening(true);
    try {
      const res = await axios.post<BaseResponseSingle<IPintagBoxStatus>>(
        OPEN_PINTAG_BOX,
        {
          pintagColorId: confirm.pintagColorId,
          openedByUserId,
        },
      );
      if (res.data?.status !== "SUCCESS") {
        throw new Error(res.data?.message || "Could not open a new box");
      }
      const opened = res.data.body;
      setBoxes((list) =>
        list.map((b) =>
          b.pintagColorId === opened.pintagColorId ? opened : b,
        ),
      );
      snackbar.showSnackbar(
        `${opened.description || opened.alias}: Box ${opened.boxNumber} opened`,
        "success",
      );
      setConfirm(null);
    } catch (err: any) {
      snackbar.showSnackbar(
        err?.message || "Could not open a new box",
        "error",
      );
    } finally {
      setOpening(false);
    }
  };

  const cellSx = { fontSize: "var(--fs-base)" };
  const headSx = {
    fontSize: "var(--fs-xs)",
    fontWeight: 600,
    color: Colors.TEXT_GRAY_DARK,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Table aria-label="Pin tag boxes">
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Color</TableCell>
            <TableCell sx={headSx}>Description</TableCell>
            <TableCell sx={headSx}>Identification</TableCell>
            <TableCell sx={headSx}>Current Box</TableCell>
            <TableCell sx={headSx}>Opened</TableCell>
            <TableCell sx={headSx}>Tags Used</TableCell>
            <TableCell sx={headSx} align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          )}
          {!loading && boxes.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={cellSx}>
                No pin tag colours
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            boxes.map((box) => (
              <TableRow
                key={box.pintagColorId}
                data-testid={`pintag-box-${box.alias}`}
              >
                <TableCell>
                  <Box
                    sx={{
                      width: "6.6rem",
                      height: "1.3rem",
                      background: box.color,
                      border: `1px solid ${Colors.MAIN_GRAY}`,
                    }}
                  />
                </TableCell>
                <TableCell sx={cellSx}>{box.description}</TableCell>
                <TableCell sx={cellSx}>{box.alias}</TableCell>
                <TableCell sx={cellSx}>Box {box.boxNumber}</TableCell>
                <TableCell sx={cellSx}>
                  {box.openedAt
                    ? dayjs(box.openedAt).format("MMM D, YYYY")
                    : "—"}
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "var(--fs-xs)",
                      color: Colors.TEXT_GRAY_DARK,
                    }}
                  >
                    {box.autoOpened
                      ? "Opened automatically"
                      : box.openedBy || ""}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...cellSx, minWidth: "14rem" }}>
                  {box.used} / {TAGS_PER_BOX}
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (box.used / TAGS_PER_BOX) * 100)}
                    color={box.remaining <= 50 ? "error" : "primary"}
                    sx={{
                      mt: "0.4rem",
                      height: "0.5rem",
                      borderRadius: "0.25rem",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "var(--fs-xs)",
                      color: Colors.TEXT_GRAY_DARK,
                    }}
                  >
                    {box.remaining} left
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <CustomButton
                    sx={{ px: "2rem", fontSize: "var(--fs-base)" }}
                    onClick={() => setConfirm(box)}
                    aria-label={`Open new box for ${box.description || box.alias}`}
                  >
                    Open New Box
                  </CustomButton>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      <Dialog open={!!confirm} onClose={() => !opening && setConfirm(null)}>
        <DialogTitle sx={{ fontSize: "var(--fs-lg)" }}>
          Open a new pin tag box?
        </DialogTitle>
        <DialogContent>
          {confirm && (
            <Typography sx={{ fontSize: "var(--fs-base)" }}>
              {confirm.description || confirm.alias} moves from Box{" "}
              {confirm.boxNumber} to Box {confirm.boxNumber + 1}. Its books
              stay closed until a manager releases them on the POS (Pin Tag
              Boxes → Books); releasing a book disables the same numbers left in Box{" "}
              {confirm.boxNumber}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: "2rem", pb: "1.5rem", gap: "1rem" }}>
          <CustomButton
            sx={{
              px: "2rem",
              fontSize: "var(--fs-base)",
              background: Colors.MAIN_GRAY,
              color: Colors.WHITE,
            }}
            onClick={() => setConfirm(null)}
            disabled={opening}
          >
            Cancel
          </CustomButton>
          <CustomButton
            sx={{ px: "2rem", fontSize: "var(--fs-base)" }}
            onClick={handleOpenBox}
            disabled={opening}
          >
            {opening ? (
              <CircularProgress size="1.6rem" />
            ) : (
              `Open Box ${confirm ? confirm.boxNumber + 1 : ""}`
            )}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
